import React, { useEffect, useState } from 'react'
import { Alert, Box, Switch, Typography } from '@mui/material'
import { getConsentStatus, setMarketingEmailPreference } from '../../consent/consentService'
import {
  EMAIL_PREFERENCES_COPY,
  PROFILE_MARKETING_WORDING_VERSION,
} from '../../consent/consentCopy'

type LoadState = 'loading' | 'ready' | 'unavailable'

/**
 * Profile → Password & Security: the marketing email choice (SC-360).
 *
 * Each toggle saves straight away instead of waiting for the dialog's Save button, because
 * the choice is a consent record with its own timestamp, not a profile field.
 */
const EmailPreferencesPanel: React.FC = () => {
  const [loadState, setLoadState] = useState<LoadState>('loading')
  const [optIn, setOptIn] = useState(false)
  const [saving, setSaving] = useState(false)
  const [notice, setNotice] = useState<'saved' | 'failed' | null>(null)

  useEffect(() => {
    let cancelled = false
    getConsentStatus()
      .then((status) => {
        if (cancelled) return
        // No user block means the call carried no session we can save against.
        if (!status?.user) {
          setLoadState('unavailable')
          return
        }
        // Never asked (null) is shown as Off: nobody is opted in without saying so.
        setOptIn(status.user.marketingEmailOptIn === true)
        setLoadState('ready')
      })
      .catch((err) => {
        console.warn('[EmailPreferencesPanel] Could not load the email preference:', err)
        if (!cancelled) setLoadState('unavailable')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleToggle = async (next: boolean) => {
    setOptIn(next)
    setSaving(true)
    setNotice(null)
    try {
      const result = await setMarketingEmailPreference({
        optIn: next,
        wordingVersion: PROFILE_MARKETING_WORDING_VERSION,
      })
      setOptIn(typeof result?.marketingEmailOptIn === 'boolean' ? result.marketingEmailOptIn : next)
      setNotice('saved')
    } catch (err) {
      console.warn('[EmailPreferencesPanel] Saving the email preference failed:', err)
      // Show what is actually recorded, which is still the previous value.
      setOptIn(!next)
      setNotice('failed')
    } finally {
      setSaving(false)
    }
  }

  const disabled = loadState !== 'ready' || saving

  return (
    <Box sx={{ py: 2, maxWidth: 520 }} data-testid="email-preferences-panel">
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
        {EMAIL_PREFERENCES_COPY.heading}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={600}>
            {EMAIL_PREFERENCES_COPY.rowLabel}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {EMAIL_PREFERENCES_COPY.rowDescription}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
          <Typography variant="body2" color="text.secondary" data-testid="email-preferences-state">
            {optIn ? EMAIL_PREFERENCES_COPY.on : EMAIL_PREFERENCES_COPY.off}
          </Typography>
          <Switch
            checked={optIn}
            disabled={disabled}
            onChange={(e) => void handleToggle(e.target.checked)}
            inputProps={{ 'aria-label': EMAIL_PREFERENCES_COPY.switchAriaLabel }}
          />
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        {EMAIL_PREFERENCES_COPY.serviceNote}
      </Typography>

      {loadState === 'unavailable' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          {EMAIL_PREFERENCES_COPY.unavailable}
        </Alert>
      )}
      {notice === 'saved' && (
        <Alert severity="success" sx={{ mt: 2 }} role="status">
          {EMAIL_PREFERENCES_COPY.saved}
        </Alert>
      )}
      {notice === 'failed' && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {EMAIL_PREFERENCES_COPY.saveFailed}
        </Alert>
      )}
    </Box>
  )
}

export default EmailPreferencesPanel
